import React, { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { reviewService } from '../api/reviewService';
import Pagination from '../components/Common/Pagination';
import '../components/Common/Common.css';

const STATUS_LABEL = {
  pending: { label: 'En attente', color: '#856404', bg: '#fff3cd' },
  approved: { label: 'Approuvé', color: '#155724', bg: '#d4edda' },
  rejected: { label: 'Rejeté', color: '#721c24', bg: '#f8d7da' },
};

function Stars({ rating }) {
  return (
    <span style={{ color: '#f59e0b', letterSpacing: 1 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n}>{n <= rating ? '★' : '☆'}</span>
      ))}
    </span>
  );
}

function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [actionId, setActionId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [rejectModal, setRejectModal] = useState(null); // { id }
  const [rejectNotes, setRejectNotes] = useState('');

  useEffect(() => {
    fetchReviews();
  }, [statusFilter, currentPage]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage };
      if (statusFilter) params.status = statusFilter;
      const data = await reviewService.list(params);
      setReviews(data.data || []);
      setLastPage(data.last_page || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec du chargement des avis');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setActionId(id);
    setError('');
    setMessage('');
    try {
      await reviewService.approve(id);
      setMessage('Avis approuvé');
      setTimeout(() => setMessage(''), 2500);
      fetchReviews();
    } catch (err) {
      setError(err.response?.data?.message || "Échec de l'approbation");
    } finally {
      setActionId(null);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectModal) return;
    setActionId(rejectModal.id);
    setError('');
    setMessage('');
    try {
      await reviewService.reject(rejectModal.id, rejectNotes);
      setMessage('Avis rejeté');
      setRejectModal(null);
      setRejectNotes('');
      setTimeout(() => setMessage(''), 2500);
      fetchReviews();
    } catch (err) {
      setError(err.response?.data?.message || 'Échec du rejet');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Modération des avis clients</h1>
      </div>

      {message && (
        <div className="success-message" style={{ marginBottom: 20, padding: 12, background: '#d4edda', color: '#155724', borderRadius: 6 }}>
          {message}
        </div>
      )}
      {error && (
        <div className="error-message" style={{ marginBottom: 20, padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6 }}>
          {error}
        </div>
      )}

      {/* Filter */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 600 }}>Statut :</label>
          {['pending', 'approved', 'rejected', ''].map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => {
                setStatusFilter(s);
                setCurrentPage(1);
              }}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid #ced4da',
                background: statusFilter === s ? '#0d6efd' : '#fff',
                color: statusFilter === s ? '#fff' : '#333',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {s === '' ? 'Tous' : STATUS_LABEL[s].label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="loading">Chargement...</div>
      ) : reviews.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 40, color: '#666' }}>
            Aucun avis {statusFilter ? `(${STATUS_LABEL[statusFilter]?.label.toLowerCase()})` : ''} pour le moment.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {reviews.map((r) => {
            const status = STATUS_LABEL[r.status] || STATUS_LABEL.pending;
            const authorName = r.client ? `${r.client.first_name || ''} ${r.client.last_name || ''}`.trim() || r.client.email : r.guest_name;
            const authorEmail = r.client ? r.client.email : r.guest_email;
            return (
              <div key={r.id} className="card">
                <div className="card-body" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <Stars rating={r.rating} />
                        <span style={{ fontWeight: 600 }}>{authorName || 'Anonyme'}</span>
                        {r.is_verified_purchase && (
                          <span style={{ fontSize: 11, padding: '2px 8px', background: '#e6f4ea', color: '#137333', borderRadius: 99 }}>
                            Achat vérifié
                          </span>
                        )}
                        <span style={{ fontSize: 11, padding: '2px 8px', background: status.bg, color: status.color, borderRadius: 99 }}>
                          {status.label}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {authorEmail} · {new Date(r.created_at).toLocaleString('fr-FR')}
                        {r.product && <> · Produit : <strong>{r.product.name}</strong></>}
                      </div>
                    </div>
                    {r.status === 'pending' && (
                      <div className="action-buttons">
                        <button
                          type="button"
                          onClick={() => handleApprove(r.id)}
                          disabled={actionId === r.id}
                          className="btn btn-small btn-success"
                        >
                          <Check size={15} />
                          Approuver
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRejectModal({ id: r.id }); setRejectNotes(''); }}
                          disabled={actionId === r.id}
                          className="btn btn-small btn-delete"
                        >
                          <X size={15} />
                          Rejeter
                        </button>
                      </div>
                    )}
                  </div>

                  {r.title && (
                    <h4 style={{ margin: '4px 0', fontSize: 15 }}>{r.title}</h4>
                  )}
                  {r.comment && (
                    <p style={{ margin: '4px 0 0', color: '#374151', whiteSpace: 'pre-wrap' }}>{r.comment}</p>
                  )}
                  {r.admin_notes && (
                    <div style={{ marginTop: 8, padding: 8, background: '#fff3cd', borderRadius: 4, fontSize: 12 }}>
                      <strong>Note admin :</strong> {r.admin_notes}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={setCurrentPage} />
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setRejectModal(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 8, padding: 24, width: '90%', maxWidth: 500 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Rejeter l'avis</h3>
            <p style={{ color: '#666', fontSize: 14 }}>
              Optionnel : note interne expliquant le rejet (non visible par le client).
            </p>
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              rows="3"
              maxLength="500"
              style={{ width: '100%', padding: 10, border: '1px solid #ced4da', borderRadius: 6, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Ex : Contenu inapproprié, hors-sujet, spam..."
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setRejectModal(null)}
                style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleRejectConfirm}
                disabled={actionId === rejectModal.id}
                style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >
                {actionId === rejectModal.id ? '...' : 'Confirmer le rejet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reviews;
